/**
 * The asset and network choices, derived entirely from the settings response.
 *
 * Nothing here is hard-coded: no symbol, no network name, no decimals. The
 * brief for this page is explicit that the asset and network selection comes
 * from `GET /v1/invoicing/settings`, and the reason is concrete — an invoice
 * denominated in a symbol this organisation has no collection wallet for cannot
 * be issued, and a symbol typed into the client is a second, unchecked source
 * of truth that drifts from the ledger.
 *
 * Two fields of the settings response carry what is needed, and they carry
 * different halves of it:
 *
 *  - `collectionWallets[]` says which `(network, assetSymbol)` pairs the issuer
 *    can actually receive on. That is the set of assets an invoice may be
 *    denominated in, and the set of networks it will accept.
 *  - `networkLimits[]` is the only place a `decimals` appears, inside the
 *    `Money` values of `dustThreshold` / `minInvoiceAmount`.
 *
 * So the two are **joined**: an asset is offered only when its symbol has both
 * a collection wallet and a declared decimal count. An asset with a wallet but
 * no declared decimals cannot be priced — sending it would mean guessing the
 * scale, and a guessed scale is a wrong amount — so it is reported as
 * unpriced rather than silently dropped. "I do not know this asset's decimals"
 * is a fact the operator should see.
 */

import { isMoney } from "@/features/invoicing/money";
import type { Asset, InvoicingSettings, NetworkLimit } from "@/features/invoicing/types";

/** One selectable asset, with the networks the organisation can receive it on. */
export interface SelectableAsset extends Asset {
  /** Backing collection wallets for this symbol, in settings order. */
  wallets: { network: string; walletId: string; validatedAt?: string }[];
  /** Distinct networks for this symbol, in settings order. */
  networks: string[];
}

export interface AssetCatalog {
  assets: SelectableAsset[];
  /**
   * Symbols with a collection wallet but no declared decimal count anywhere in
   * `networkLimits`. Offered nowhere, and named so the gap is visible.
   */
  unpricedSymbols: string[];
}

/** The decimals a symbol's `Money` values declare, if any. */
function declaredDecimals(limits: NetworkLimit[]): Map<string, number> {
  const found = new Map<string, number>();
  for (const limit of limits) {
    for (const value of [limit.dustThreshold, limit.minInvoiceAmount]) {
      // A malformed Money is ignored rather than trusted: `isMoney` is the same
      // check the wire layer applies, so this can never read a scale out of a
      // value the contract would not accept.
      if (isMoney(value) && !found.has(value.asset.symbol)) {
        found.set(value.asset.symbol, value.asset.decimals);
      }
    }
  }
  return found;
}

export function assetCatalog(settings: InvoicingSettings): AssetCatalog {
  const decimals = declaredDecimals(settings.networkLimits ?? []);
  const wallets = settings.collectionWallets ?? [];

  const bySymbol = new Map<string, SelectableAsset>();
  const unpriced: string[] = [];

  for (const wallet of wallets) {
    const scale = decimals.get(wallet.assetSymbol);
    if (scale === undefined) {
      if (!unpriced.includes(wallet.assetSymbol)) unpriced.push(wallet.assetSymbol);
      continue;
    }
    const existing = bySymbol.get(wallet.assetSymbol);
    if (existing) {
      if (!existing.networks.includes(wallet.network)) {
        existing.networks.push(wallet.network);
        existing.wallets.push({ network: wallet.network, walletId: wallet.walletId, validatedAt: wallet.validatedAt });
      }
      continue;
    }
    bySymbol.set(wallet.assetSymbol, {
      symbol: wallet.assetSymbol,
      decimals: scale,
      networks: [wallet.network],
      wallets: [{ network: wallet.network, walletId: wallet.walletId, validatedAt: wallet.validatedAt }],
    });
  }

  return { assets: [...bySymbol.values()], unpricedSymbols: unpriced };
}
